import React, { useRef, useEffect } from 'react';
import { UserProfile, UserRole } from '../types/user';
import { isSuperAdminUser } from '../utils/rbac';
import { Language } from '../types/kundali';
import { ShieldCheck, LogOut, Check, User, Sparkles, MessageSquare, ExternalLink } from 'lucide-react';

export interface ProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile | null;
  onLogout: () => void;
  onOpenAuth?: () => void;
  onSwitchRole?: (role: UserRole) => void;
  onOpenWhatsAppModal?: () => void;
  lang?: Language | string;
}

interface TierDefinition {
  id: string;
  role: UserRole;
  label: string;
  badge: string;
  description: string;
}

const WORKSPACE_TIERS: TierDefinition[] = [
  {
    id: 'BASIC',
    role: UserRole.BASIC_USER,
    label: 'Basic Profile',
    badge: 'Basic',
    description: 'Personal jataka dossier, foundational planetary positions & life questions'
  },
  {
    id: 'PRO_ASTRO',
    role: UserRole.PRO_ASTROLOGER,
    label: 'Pro Astro',
    badge: 'Pro Astro',
    description: 'Divisional D1–D60 charts, Ashtakvarga, KP System, Shadbala & transit telemetry'
  },
  {
    id: 'ACHARYA',
    role: UserRole.CONSULTING_ASTROLOGER,
    label: 'Acharya',
    badge: 'Acharya',
    description: 'Comprehensive client consultation workbench, remedies & Muhurta synthesis'
  },
  {
    id: 'MANAGEMENT',
    role: UserRole.ADMIN,
    label: 'Management',
    badge: 'Management',
    description: 'System administration, consultation pipeline & platform governance'
  }
];

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  isOpen,
  onClose,
  currentUser,
  onLogout,
  onOpenAuth,
  onSwitchRole,
  onOpenWhatsAppModal,
  lang = 'en'
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isHi = lang === 'hi';

  // Outside click & ESC dismissal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Evaluate if user is a Super Admin authorized for cross-tier governance
  const isSuperAdmin = isSuperAdminUser(currentUser);

  // Active role detection
  const currentActiveRole = currentUser?.activeRole || currentUser?.role || UserRole.BASIC_USER;

  const isGuest = !currentUser || currentUser.role === UserRole.GUEST || (currentUser.role as string) === 'guest' || currentUser.uid?.startsWith('guest-');

  // Resolve current tier
  const getCurrentTierLabel = (): string => {
    if (isGuest) return 'Guest Workspace';
    switch (currentActiveRole) {
      case UserRole.ADMIN:
      case UserRole.SUPER_ADMIN:
        return 'Management';
      case UserRole.CONSULTING_ASTROLOGER:
        return 'Acharya';
      case UserRole.PRO_ASTROLOGER:
      case UserRole.ADVANCED_ASTROLOGY_USER:
        return 'Pro Astro';
      case UserRole.BASIC_USER:
      default:
        return 'Basic Profile';
    }
  };

  const activeTierLabel = getCurrentTierLabel();
  const userName = isGuest ? (isHi ? 'अतिथि अन्वेषक' : 'Guest Explorer') : ((currentUser as any)?.name || currentUser?.displayName || 'Vedic Practitioner');
  const userInitial = isGuest ? 'G' : (userName[0] || 'V').toUpperCase();

  return (
    <div
      ref={dropdownRef}
      id="profile-role-dropdown"
      role="menu"
      aria-label="Workstation Profile & Role Governance"
      className="fixed right-3 top-16 sm:right-6 sm:top-16 w-72 sm:w-80 bg-astro-bg border border-astro-border rounded-2xl shadow-2xl z-50 overflow-hidden animate-fade-in"
    >
      {/* 1. Header: User Identity & Active Tier */}
      <div className="p-3.5 bg-astro-card border-b border-astro-border">
        <div className="flex items-center gap-3">
          {/* Avatar Circle */}
          <div className="w-10 h-10 rounded-full bg-astro-primary text-astro-bg flex items-center justify-center font-bold font-serif text-base border border-astro-accent/40 shadow-xs shrink-0">
            {userInitial}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-sm text-astro-primary truncate font-serif">
                {userName}
              </span>
              {isSuperAdmin && (
                <span title="Super Admin Governed" className="inline-flex shrink-0">
                  <ShieldCheck className="w-3.5 h-3.5 text-astro-accent" />
                </span>
              )}
            </div>
            <p className="text-[11px] text-astro-muted truncate font-mono">
              {currentUser?.email || (currentUser ? 'jyotishi@vedang.org' : 'Guest Account')}
            </p>
          </div>
        </div>

        {/* Tier Indicator Strip */}
        <div className="mt-2.5 flex items-center justify-between pt-2 border-t border-astro-border/70 text-xs">
          <span className="text-[10px] uppercase tracking-wider text-astro-muted font-bold">
            {isHi ? 'सक्रिय वर्कस्टेशन स्तर:' : 'Active Tier:'}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-astro-bg text-astro-primary font-bold text-[10px] border border-astro-border shadow-2xs">
            {activeTierLabel}
          </span>
        </div>
      </div>

      {/* 2. Strict Role-Based Presentation Isolation */}
      <div className="p-3">
        {isSuperAdmin ? (
          /* SUPER ADMIN GOVERNANCE: Interactive 4-tier switcher */
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase font-bold tracking-wider text-astro-accent flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {isHi ? 'सुपर एडमिन टियर स्विच' : 'Super Admin Tier Governance'}
              </span>
              <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-50 text-amber-800 border border-amber-200 font-bold">
                Live Simulator
              </span>
            </div>

            <div className="space-y-2 mt-2">
              {WORKSPACE_TIERS.map((tier) => {
                const isSelected = activeTierLabel === tier.badge;
                return (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => {
                      if (onSwitchRole) {
                        onSwitchRole(tier.role);
                      }
                    }}
                    className={`w-full text-left p-2.5 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'bg-astro-card border-astro-primary shadow-xs'
                        : 'bg-astro-bg border-astro-border hover:bg-astro-card/60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-xs text-astro-primary">
                          {tier.label}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-astro-bg text-astro-accent border border-astro-border font-medium">
                          {tier.badge}
                        </span>
                      </div>
                      <p className="text-[11px] text-astro-muted line-clamp-1 leading-snug">
                        {tier.description}
                      </p>
                    </div>

                    {/* Dynamic Checkmark Indicator */}
                    <div className="shrink-0 w-5 flex items-center justify-center">
                      {isSelected && (
                        <span className="text-sm font-bold text-astro-primary">✓</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* STANDARD USERS: Suppress all cross-tier switching. Display ONLY active static badge */
          <div className="py-2 px-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase font-bold tracking-wider text-astro-muted">
                {isHi ? 'प्राधिकृत कार्यक्षेत्र' : 'Entitled Workstation Tier'}
              </span>
              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                Active
              </span>
            </div>

            <div className="p-3 rounded-xl bg-astro-card border border-astro-border text-xs">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-astro-primary text-astro-bg flex items-center justify-center font-bold text-[11px]">
                  ✓
                </div>
                <div>
                  <h4 className="font-bold text-astro-primary text-xs">
                    {activeTierLabel}
                  </h4>
                  <p className="text-[10px] text-astro-secondary-text mt-0.5">
                    {WORKSPACE_TIERS.find((t) => t.badge === activeTierLabel)?.description ||
                      'Standard authenticated Vedic astrology workspace'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Session Footer: Identity details & Sign Out */}
      <div className="p-2.5 bg-astro-card border-t border-astro-border flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-astro-muted text-[11px]">
          <User className="w-3 h-3 text-astro-accent" />
          <span>{!isGuest ? 'Active Session' : 'Guest Mode'}</span>
        </div>

        {!isGuest ? (
          <button
            type="button"
            onClick={() => {
              onClose();
              onLogout();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-rose-800 hover:text-rose-900 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition cursor-pointer"
          >
            <LogOut className="w-3 h-3" />
            <span>{isHi ? 'साइन आउट' : 'Sign Out'}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              onClose();
              if (onOpenAuth) onOpenAuth();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-astro-primary bg-astro-bg hover:bg-[#FAF6ED] border border-astro-border transition cursor-pointer"
          >
            <User className="w-3 h-3" />
            <span>{isHi ? 'लॉगिन / साइन इन' : 'Sign In'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileDropdown;
